/**
 * Lab candidate registry — features to promote into The Lab catalog.
 * Items already in labItems are kept here for taxonomy completeness but hidden
 * on the Lab page (see visibleLabCandidates in Lab.tsx).
 */
export interface LabCandidate {
  path: string;
  label: string;
  description: string;
  category: string;
  external?: boolean;
}

/** Display order for Lab Candidates section group headings. */
export const LAB_CANDIDATE_CATEGORY_ORDER = [
  'Prioritization Insights',
  'Standalone Apps',
  'Coach Tools & Frameworks',
  'Knowledge & Design',
  'Prospector & Operations',
  'Platform & Trust',
  'Sibling Apps',
  'Prioritization Tools',
] as const;

export const LAB_CANDIDATES: LabCandidate[] = [
  // Prioritization Insights
  {
    path: '/prioritization/sessions/explore',
    label: 'Event Designer (/explore)',
    description:
      'The Event Designer on its secondary route — item input, cutline scenarios, tool framing, and exclusivity rules. The Designer is already the default at /sessions/new; this entry is the extra /explore URL, behind the SESSION_ENGINE_V3 flag.',
    category: 'Prioritization Insights',
  },
  {
    path: '/elevation/lab/cognitive-bias',
    label: 'Cognitive Bias Audit',
    description:
      'AI-assisted bias detection across cycle or session context — surfaces how biases manifest in actual prioritization work.',
    category: 'Prioritization Insights',
  },
  {
    path: '/elevation/lab/lexicon',
    label: 'Lexicon',
    description:
      'Language intelligence from meeting transcripts — speaker profiles, communication patterns, and baseball-card views.',
    category: 'Prioritization Insights',
  },
  {
    path: '/prioritization/agents/nexus',
    label: 'Nexus Report',
    description:
      'Cross-item relationship and dependency nexus visualization for portfolio-level clarity.',
    category: 'Prioritization Insights',
  },
  {
    path: '/prioritization/items',
    label: 'Whiteboard Photo Import',
    description:
      'Upload a photo of a physical whiteboard — vision model parses boxes, arrows, and stickies into Items and typed relationships. Reached via Import → Whiteboard Photo on the Items list.',
    category: 'Prioritization Insights',
  },
  {
    path: '/prioritization/agents/twins',
    label: 'Twins Report',
    description:
      'Find structurally similar items — duplicates, near-duplicates, and alignment twins.',
    category: 'Prioritization Insights',
  },
  // Standalone Apps
  {
    path: '/planning',
    label: 'Planning App',
    description:
      'Work graph and spreadsheet views for sequencing, capacity, and plan-of-record alignment.',
    category: 'Standalone Apps',
  },
  {
    path: '/apps/mandate',
    label: 'Mandate App',
    description:
      'Situation → outcome mandate framing for scoped delegation and authority boundaries.',
    category: 'Standalone Apps',
  },
  {
    path: '/brief',
    label: 'Brief App',
    description:
      'Structured brief intake, review, and decision panel for async stakeholder alignment.',
    category: 'Standalone Apps',
  },
  // Coach Tools & Frameworks
  {
    path: '/elevation/ladder-of-inference',
    label: 'Ladder of Inference',
    description:
      'Interactive climb from observable data to conclusions — separate facts from judgments.',
    category: 'Coach Tools & Frameworks',
  },
  {
    path: '/elevation/ladder-of-inference-m2-model',
    label: 'Ladder of Inference (M2 Model)',
    description: 'Full M2 model view of the inference ladder with semantic annotations.',
    category: 'Coach Tools & Frameworks',
  },
  {
    path: '/elevation/johari-window',
    label: 'Johari Window',
    description:
      'Known/unknown self and others — feedback and disclosure for team trust building.',
    category: 'Coach Tools & Frameworks',
  },
  // Knowledge & Design
  {
    path: '/elevation/artifact-gallery',
    label: 'Artifact Gallery',
    description:
      'Browse prioritization artifacts, templates, and example outputs across the system.',
    category: 'Knowledge & Design',
  },
  {
    path: '/elevation/documentation-hub',
    label: 'Documentation Hub',
    description: 'Central index for product docs, runbooks, and in-app documentation areas.',
    category: 'Knowledge & Design',
  },
  {
    path: '/elevation/glossary',
    label: 'Glossary',
    description:
      'Canonical terminology for prioritization, planning, and the Peak Priorities system.',
    category: 'Knowledge & Design',
  },
  // Prospector & Operations
  {
    path: '/elevation/notion-intake',
    label: 'Notion Intake',
    description: 'Rules-driven pipeline for capturing Notion content into the knowledge system.',
    category: 'Prospector & Operations',
  },
  // Platform & Trust
  {
    path: '/trust',
    label: 'Trust Center',
    description: 'Security, subprocessors, and compliance documentation for enterprise buyers.',
    category: 'Platform & Trust',
  },
  // Sibling Apps
  {
    path: 'http://localhost:3202',
    label: 'Leader Onboarding App',
    description:
      'New-leader onboarding arc — rhythm, cascade, and readiness steps (local dev :3202).',
    category: 'Sibling Apps',
    external: true,
  },
  {
    path: 'http://localhost:3203',
    label: 'Parachute App',
    description: 'Bolles-style career design — skills, style, search, and launch (local dev :3203).',
    category: 'Sibling Apps',
    external: true,
  },
  // Prioritization Tools
  {
    path: '/eisenhower-matrix',
    label: 'Eisenhower Matrix',
    description:
      'Urgent vs important quadrant prioritization — standalone tool outside the tools hub.',
    category: 'Prioritization Tools',
  },
  {
    path: '/rice-scoring',
    label: 'RICE Scoring',
    description: 'Reach, Impact, Confidence, Effort scoring for product-style backlog ranking.',
    category: 'Prioritization Tools',
  },
  {
    path: '/simple-weighted-scoring',
    label: 'Simple Weighted Scoring',
    description:
      'Criteria-weighted pseudo-quantitative scoring with explicit judgment transparency.',
    category: 'Prioritization Tools',
  },
  {
    path: '/pairwise-ranking',
    label: 'Pairwise Ranking',
    description: 'Head-to-head comparisons to build a ranked list from pairwise preferences.',
    category: 'Prioritization Tools',
  },
  {
    path: '/stack-ranking',
    label: 'Stack Ranking',
    description: 'Drag-and-drop forced ordering for small sets where relative rank matters most.',
    category: 'Prioritization Tools',
  },
];

/** Candidates not yet linked from labItems (main catalog). */
export function visibleLabCandidates(catalogPaths: Iterable<string>): LabCandidate[] {
  const paths = new Set(catalogPaths);
  return LAB_CANDIDATES.filter((candidate) => !paths.has(candidate.path));
}

/** Group candidates by category for display, in canonical category order. */
export function labCandidatesByCategory(
  candidates: LabCandidate[] = LAB_CANDIDATES,
): Map<string, LabCandidate[]> {
  const map = new Map<string, LabCandidate[]>();
  for (const candidate of candidates) {
    const list = map.get(candidate.category) ?? [];
    list.push(candidate);
    map.set(candidate.category, list);
  }

  const ordered = new Map<string, LabCandidate[]>();
  for (const category of LAB_CANDIDATE_CATEGORY_ORDER) {
    const list = map.get(category);
    if (list && list.length > 0) ordered.set(category, list);
  }
  for (const [category, list] of map) {
    if (!ordered.has(category)) ordered.set(category, list);
  }
  return ordered;
}
